import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import { formatIndianCurrency, formatIndianGrouping, formatPct } from "@/lib/format";
import KpiTile from "@/components/KpiTile";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { AlertTriangle, TrendingDown, Package2 } from "lucide-react";

const TONES = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

export default function StockOverview() {
  const { data } = useSWR("/stock/dashboard", fetcher);
  const { data: dim } = useSWR("/stock/dead-stock-by-dimension", fetcher);
  if (!data) return null;
  const fan = data.fan_split;
  const totalFan = fan.fast + fan.average + fan.non_moving || 1;
  const pie = [
    { name: "Fast", value: fan.fast },
    { name: "Average", value: fan.average },
    { name: "Non-moving", value: fan.non_moving },
  ];
  const fastPct = (fan.fast / totalFan) * 100;
  const nonMovingShare = (fan.non_moving / totalFan) * 100;
  const deadShareValue = data.total_value ? (data.dead_stock_value / data.total_value) * 100 : 0;

  return (
    <div>
      <PageHeader
        eyebrow="Stock Intelligence"
        title="Health of every shelf."
        lede="Aggregate posture across 8,200 SKUs. Watch the non-moving share — that's parked cash."
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
        <KpiTile testId="kpi-total-skus" label="Total SKUs" kind="number" value={data.total_skus} />
        <KpiTile testId="kpi-total-value" label="Stock value" value={data.total_value} accent />
        <KpiTile testId="kpi-dead-value" label="Dead stock" value={data.dead_stock_value} sub={`${deadShareValue.toFixed(1)}% of inventory`} />
        <KpiTile testId="kpi-nonmoving-pct" label="Non-moving" kind="pct" value={data.non_moving_pct} sub={`${formatIndianGrouping(fan.non_moving)} SKUs`} />
      </div>

      {/* Narrative analysis strip */}
      <div className="grid md:grid-cols-3 gap-px bg-border mt-10">
        <div className="bg-background p-5">
          <div className="flex items-center gap-2 mb-2"><Package2 className="w-4 h-4 text-positive" /><span className="label-caps">Healthy</span></div>
          <div className="font-display text-xl">{fastPct.toFixed(0)}% of SKUs are fast-moving</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {formatIndianGrouping(fan.fast)} products turning at &gt;3 units/day. Keep replenishment cycles aggressive on this cohort.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="flex items-center gap-2 mb-2"><TrendingDown className="w-4 h-4 text-warning" /><span className="label-caps">At risk</span></div>
          <div className="font-display text-xl">{nonMovingShare.toFixed(0)}% by SKU count, {deadShareValue.toFixed(0)}% by value</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {formatIndianCurrency(data.dead_stock_value)} sits in non-moving inventory. Bundle, markdown or write-off — decide this quarter.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-destructive" /><span className="label-caps">Critical</span></div>
          <div className="font-display text-xl">{data.critical_alerts?.length || 0} SKUs running out</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Products with &lt;200 units cover and &gt;0.5/day velocity. PO must move within 48h to avoid empty shelves.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-10 mt-14">
        <Section eyebrow="F / A / N" title="The shape of the floor">
          <div className="hairline p-4">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pie} dataKey="value" innerRadius={70} outerRadius={110} stroke="hsl(var(--background))" strokeWidth={3}>
                  {pie.map((_, i) => <Cell key={i} fill={TONES[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-around mt-3">
              {pie.map((p, i) => (
                <div key={p.name} className="text-center">
                  <div className="w-3 h-3 mx-auto" style={{ background: TONES[i] }} />
                  <div className="label-caps mt-1">{p.name}</div>
                  <div className="font-mono text-sm">{formatIndianGrouping(p.value)}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
        <Section eyebrow="At risk of stockout" title="Critical alerts">
          <div className="space-y-1">
            {(data.critical_alerts || []).map((a) => (
              <div key={a.name} className="hairline px-4 py-3 flex items-start justify-between">
                <div className="min-w-0 flex-1 pr-3">
                  <div className="font-display text-base truncate" title={a.name}>{a.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{a.department} · {a.vendor_name}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm">{formatIndianGrouping(a.closing_qty)} left</div>
                  <div className="text-[11px] text-warning">{a.daily_sales_rate}/day</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Dead stock — by dimension */}
      <div className="grid md:grid-cols-2 gap-10 mt-14">
        <Section eyebrow="Concentration" title="Dead stock by department">
          <div className="hairline p-4">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={(dim?.by_department || []).slice(0, 8)} layout="vertical">
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => formatIndianCurrency(v)} />
                <YAxis type="category" dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} width={100} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => formatIndianCurrency(v)} />
                <Bar dataKey="value" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section eyebrow="Concentration" title="Top 10 vendors holding dead stock">
          <div className="hairline">
            <table className="w-full table-dense">
              <thead>
                <tr><th>Vendor</th><th className="text-right">SKUs</th><th className="text-right">Parked cash</th></tr>
              </thead>
              <tbody>
                {(dim?.by_vendor || []).map((v) => (
                  <tr key={v.label}>
                    <td className="font-display text-sm cell-truncate-lg" title={v.label}>{v.label}</td>
                    <td className="text-right">{v.skus}</td>
                    <td className="text-right text-warning">{formatIndianCurrency(v.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}
